import {
  Banknote,
  TrendingUp,
  Wallet,
  CreditCard,
  Truck,
  ReceiptText,
  RefreshCw,
  ArrowUpRight,
  MessageCircle,
} from "lucide-react";
import { openLiveChat } from "../../lib/chat";
import { cn } from "../../lib/utils";

const products = [
  {
    id: "sba_loans",
    icon: Banknote,
    title: "SBA Loans",
    range: "$25K – $5M",
    pun: "The slow-cooked masterpiece of business loans.",
    desc: "Government-backed loans with the lowest rates and longest terms. Perfect for established businesses ready to play the long game.",
    span: "md:col-span-2",
  },
  {
    id: "commercial_real_estate_loans",
    icon: TrendingUp,
    title: "Commercial Real Estate Loans",
    range: "$250K – $20M",
    pun: "Own the ground you stand on.",
    desc: "Own your business location and build equity in your own property. Permanent headquarters secured—the keys are yours.",
    span: "",
  },
  {
    id: "line_of_credit",
    icon: CreditCard,
    title: "Lines of Credit",
    range: "$10K – $250K",
    pun: "Cash on tap. Pour as needed.",
    desc: "Revolving credit you can draw from anytime. Pay interest only on what you actually use. Flexible and friendly.",
    span: "",
  },
  {
    id: "equipment_financing",
    icon: Truck,
    title: "Equipment Financing",
    range: "$5K – $2M",
    pun: "The truck. The oven. The whole rig.",
    desc: "Finance new or used equipment with the equipment itself as collateral. Easy approval, fast close.",
    span: "md:col-span-2",
  },
  {
    id: "refinancing",
    icon: RefreshCw,
    title: "Refinancing",
    range: "Up to $5M",
    pun: "Trade old terms for great ones.",
    desc: "Lower your monthly payments and grab better terms on your existing business debt. Breathing room, restored.",
    span: "",
  },
  {
    id: "working_capital",
    icon: Wallet,
    title: "Working Capital",
    range: "$5K – $500K",
    pun: "For when payroll meets payday.",
    desc: "Short-term funding to cover payroll, inventory, and the day-to-day. Keep the lights on. Keep going.",
    span: "",
  },
  {
    id: "invoice_factoring",
    icon: ReceiptText,
    title: "Invoice Factoring",
    range: "$10K – $5M",
    pun: "Stop chasing checks. Start running.",
    desc: "Get paid now on outstanding invoices. No more 30-, 60-, or 90-day waits. Cash flow, unblocked.",
    span: "",
  },
];

export default function Products() {
  return (
    <section
      id="funding"
      data-testid="products-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-muted/40 via-transparent to-muted/25"
        aria-hidden
      />
      <div className="container-tight relative">
        <div className="max-w-3xl mb-14 md:mb-20">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B82E]">
            Funding Buffet
          </span>
          <h2
            data-testid="products-headline"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-foreground leading-[1.05] text-balance"
          >
            Pick Your Flavor.
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Whatever your business needs, we&apos;ve got the olive… er,{" "}
            <em>all of it</em>. Tell us what you&apos;re building and we&apos;ll
            match you to the product (or stack of products) that actually fits.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7">
          {products.map((p, i) => (
            <ProductCard key={p.id} {...p} staggerIndex={i} />
          ))}
        </div>

        <div
          data-testid="products-help-cta"
          className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-dashed border-border bg-muted/25 px-6 py-5"
        >
          <p className="text-sm text-muted-foreground max-w-lg">
            <span className="font-semibold text-foreground">Not sure which flavor?</span>{" "}
            Chat with us—we&apos;ll match you to the right product before you
            spend time on an application.
          </p>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openLiveChat}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/15 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Live chat
            </button>
            <a
              href="#apply"
              className="inline-flex items-center justify-center rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground hover:bg-[#D1664A] transition-colors"
            >
              Go to apply
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductCard({ icon: Icon, title, pun, desc, range, span = "", staggerIndex = 0 }) {
  return (
    <a
      href="#apply"
      data-testid={`product-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ "--olive-stagger": `${Math.min(staggerIndex, 10) * 70}ms` }}
      className={cn(
        "olive-fade-stagger group relative bg-card border border-border rounded-3xl p-8 md:p-10 flex flex-col",
        "transition-[transform,box-shadow,border-color] duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]",
        "hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgb(0,0,0,0.08)] hover:border-primary/25",
        span
      )}
    >
      <div className="flex items-start justify-between">
        <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary transition-transform duration-300 ease-out group-hover:scale-110 group-hover:bg-primary/[0.14]">
          <Icon className="h-5 w-5" strokeWidth={2.2} />
        </div>
        <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-accent group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
      </div>

      <h3 className="mt-7 font-display text-2xl md:text-3xl font-bold tracking-tight text-foreground">
        {title}
      </h3>
      <p className="mt-2 text-sm font-medium italic text-accent">{pun}</p>
      <p className="mt-4 text-base text-muted-foreground leading-relaxed">{desc}</p>
      {range && (
        <div className="mt-6 pt-5 border-t border-border flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Funding range
          </span>
          <span className="font-display text-lg font-extrabold text-primary">
            {range}
          </span>
        </div>
      )}
    </a>
  );
}
